import { useRef } from "react";

import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";

export const DRAFT_GRAPH_POLL_INTERVAL_MS = 4_000;
/** Hard stop: a graph that hasn't landed in five minutes isn't coming. */
export const DRAFT_GRAPH_POLL_MAX_MS = 5 * 60_000;

/**
 * The polling policy, pure so it can be specced without a query client:
 * poll while the caller says the graph is still being built, but never past
 * the cap measured from when that condition first became true.
 */
export const draftGraphPollInterval = ({
  active,
  startedAtTs,
  now,
}: {
  active: boolean;
  startedAtTs: number | null;
  now: number;
}): number | false =>
  active && startedAtTs !== null && now - startedAtTs < DRAFT_GRAPH_POLL_MAX_MS
    ? DRAFT_GRAPH_POLL_INTERVAL_MS
    : false;

/**
 * Polls the rule's draft while its selection-logic graph is being built
 * out-of-band — the Atlas chat's backend save, the repair loop's
 * regenerations, the SQS regen worker — none of which run through this
 * browser's mutations, so nothing else would refresh the cached
 * `{draft: null}` the skeleton canvas is sitting on.
 *
 * Mounts an extra observer on the shared draft query key; TanStack polls at
 * the smallest interval among observers, so this affects nothing while
 * `active` is false. Deactivating resets the cap window.
 */
export const useDraftGraphPoll = (ruleId: string, active: boolean) => {
  const startedAtRef = useRef<number | null>(null);
  if (!active) {
    startedAtRef.current = null;
  } else if (startedAtRef.current === null) {
    startedAtRef.current = Date.now();
  }

  useAgenticLogicRuleDraft(ruleId, {
    refetchInterval: () =>
      draftGraphPollInterval({
        active,
        startedAtTs: startedAtRef.current,
        now: Date.now(),
      }),
  });
};
